import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

/**
 * Service worker registration (Bez, 2026-10-07). The worker's version is the Next.js build id: /sw.js?v=<buildId>. A deploy
 * produces a new build id, so the first page load after it installs a new worker, which takes over every open tab and deletes the
 * old caches (public/sw.js). A tab still running the previous build is then "stale" and moves to the new build at the next safe
 * moment: its next in-app navigation becomes a full page load, or, after 30 min in the background, it reloads when shown again.
 * It never reloads on its own while visible: a wallet signature or a pending transaction screen is never interrupted.
 *
 * NEXT_PUBLIC_PWA_SW=0 (build time) is the kill switch: every visitor's worker is unregistered on the next page load.
 */
export const SW_ENABLED = process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_PWA_SW !== '0'
export const LONG_AWAY_MS = 30 * 60_000

export const swUrl = (buildId: string) => `/sw.js?v=${encodeURIComponent(buildId)}`

export function versionOf(scriptURL?: string | null): string | null {
  if (!scriptURL) return null
  try {
    const u = new URL(scriptURL)
    return u.pathname === '/sw.js' ? u.searchParams.get('v') : null
  } catch {
    return null
  }
}

function markStale() {
  document.documentElement.dataset.pwaStale = '1'
}
const isStale = () => document.documentElement.dataset.pwaStale === '1'
const manifestUrl = (buildId: string) => `/_next/static/${buildId}/_buildManifest.js`

let registered = false
export function __resetRegistrationForTests() {
  registered = false
}

type SwContainer = Pick<ServiceWorkerContainer, 'register' | 'controller'>

/**
 * Registers this page's worker ONCE per page. A page whose build is not the one the server serves any more (another tab already
 * installed a newer worker and this build's manifest answers 404, or cannot be checked) never registers: it would put the old
 * worker back over the new one (seen on Stage r169 -> r170 when a route change re-ran the registration of a stale tab).
 */
export async function registerServiceWorker(sw: SwContainer, buildId: string) {
  if (registered) return
  registered = true
  const current = versionOf(sw.controller?.scriptURL)
  if (current && current !== buildId) {
    const r = await fetch(manifestUrl(buildId), { method: 'HEAD', cache: 'no-store' }).catch(() => null)
    if (!r || r.status !== 200) {
      if (r && r.status === 404) markStale()
      return
    }
  }
  await sw.register(swUrl(buildId), { scope: '/', updateViaCache: 'none' }).catch(() => undefined)
}

export function useServiceWorker() {
  const router = useRouter()
  // router.events is one shared emitter; the effect must run once per page (the router object changes on every route change)
  const events = useRef(router.events)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined
    const sw = navigator.serviceWorker
    if (!SW_ENABLED) {
      sw.getRegistrations()
        .then((regs) => regs.forEach((r) => versionOf((r.active || r.waiting || r.installing)?.scriptURL) !== null && r.unregister()))
        .catch(() => undefined)
      return undefined
    }
    const buildId: string | undefined = (window as any).__NEXT_DATA__?.buildId
    if (!buildId) return undefined

    const onControllerChange = () => {
      const v = versionOf(sw.controller?.scriptURL)
      if (v && v !== buildId) markStale()
    }
    sw.addEventListener('controllerchange', onControllerChange)
    const register = () => {
      registerServiceWorker(sw, buildId)
    }
    if (document.readyState === 'complete') register()
    else window.addEventListener('load', register, { once: true })

    // a deploy removes the previous build's files: its build manifest answering 404 means this tab runs an old build
    let lastCheck = Date.now()
    let hiddenAt = 0
    const checkDeploy = () =>
      fetch(manifestUrl(buildId), { method: 'HEAD', cache: 'no-store' })
        .then((r) => {
          if (r.status === 404) markStale()
        })
        .catch(() => undefined)
    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      const longAway = hiddenAt > 0 && Date.now() - hiddenAt > LONG_AWAY_MS
      if (longAway || Date.now() - lastCheck > 60_000) {
        lastCheck = Date.now()
        await checkDeploy()
      }
      if (longAway && isStale()) window.location.reload()
    }
    const onRouteStart = (url: string) => {
      if (isStale()) window.location.assign(url)
    }
    const routerEvents = events.current
    document.addEventListener('visibilitychange', onVisibility)
    routerEvents.on('routeChangeStart', onRouteStart)
    return () => {
      sw.removeEventListener('controllerchange', onControllerChange)
      window.removeEventListener('load', register)
      document.removeEventListener('visibilitychange', onVisibility)
      routerEvents.off('routeChangeStart', onRouteStart)
    }
  }, [])
}
